import { CURRENT_APP_VERSION } from '../version';
import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { loginWithCredentials } from '../services/api';
import {
  Settings,
  X,
  Key,
  Home,
  ShieldCheck,
  CheckCircle2,
  Bell,
  Video,
  Wrench
} from 'lucide-react';
import { triggerHaptic } from '../services/purrSynthesizer';
import { UserProfile, UserRole } from '../types';

interface HouseholdMemberCard {
  id: UserProfile;
  displayName: string;
  subtitle: string;
  badge: string;
  emoji: string;
  role: UserRole;
  defaultPasscode: string;
  accentClass: string;
}

const HOUSEHOLD_PROFILES: HouseholdMemberCard[] = [
  {
    id: 'lycan',
    displayName: 'Lycan (Sharon)',
    subtitle: 'Perimeter Cameras, Alert Clips, Intercom & Self-Healing',
    badge: 'Family Guardian',
    emoji: '🌸',
    role: 'family',
    defaultPasscode: 'family',
    accentClass: 'from-emerald-500/20 to-teal-500/10 border-emerald-500/40 hover:border-emerald-400 text-emerald-300'
  },
  {
    id: 'onyx',
    displayName: 'Onyx',
    subtitle: 'Perimeter Cameras, Smart Home, Alerts & Fissal AI',
    badge: 'Family Guardian',
    emoji: '🐾',
    role: 'family',
    defaultPasscode: 'family',
    accentClass: 'from-purple-500/20 to-indigo-500/10 border-purple-500/40 hover:border-purple-400 text-purple-300'
  },
  {
    id: 'trey',
    displayName: 'Trey',
    subtitle: 'Cameras, Network Diagnostics, Self-Healing & DevOps',
    badge: 'Family Guardian',
    emoji: '🛡️',
    role: 'family',
    defaultPasscode: 'family',
    accentClass: 'from-sky-500/20 to-blue-500/10 border-sky-500/40 hover:border-sky-400 text-sky-300'
  },
  {
    id: 'echo',
    displayName: 'Echo',
    subtitle: 'Master of Keys — Full Family Suite, Server DevOps & USB Ops',
    badge: 'Owner',
    emoji: '🗝️',
    role: 'admin',
    defaultPasscode: 'echo1313',
    accentClass: 'from-amber-500/25 to-yellow-500/10 border-amber-500/50 hover:border-amber-400 text-amber-300'
  }
];

export const IdentityAuthModal: React.FC = () => {
  const {
    setSettingsOpen,
    setNotificationModalOpen,
    authUser,
    profile,
    authModalOpen,
    setAuthModalOpen,
    loginSession,
    setActiveLayer,
    setActiveTab
  } = useApp();

  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [statusNote, setStatusNote] = useState<string | null>(null);

  if (!authModalOpen) return null;

  const activeId = (authUser?.userId || profile || 'lycan').toLowerCase();

  const handleSelectProfile = async (member: HouseholdMemberCard) => {
    setLoadingId(member.id);
    setStatusNote(null);
    triggerHaptic([20, 40]);

    try {
      const res = await loginWithCredentials(member.id, member.defaultPasscode);
      if (res.ok && res.session) {
        loginSession(res.session);
      } else {
        // Offline / LAN direct fallback so household members are never locked out
        loginSession({
          ok: true,
          token: `local_${member.id}_${Date.now()}`,
          user: {
            userId: member.id,
            displayName: member.displayName,
            role: member.role,
            title: member.badge
          },
          shizukuEnabled: true
        });
      }
      if (member.role === 'family') {
        setActiveLayer('family');
        setActiveTab('cameras');
      }
      triggerHaptic([30, 60, 30]);
    } catch {
      loginSession({
        ok: true,
        token: `local_${member.id}_${Date.now()}`,
        user: {
          userId: member.id,
          displayName: member.displayName,
          role: member.role,
          title: member.badge
        },
        shizukuEnabled: true
      });
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3.5 bg-black/80 backdrop-blur-md animate-in fade-in duration-150">
      <div className="w-full max-w-sm rounded-3xl bg-[#0b1017] border border-fissal-gold/40 shadow-2xl p-4 text-slate-100 flex flex-col gap-3.5 max-h-[92vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-300 shadow-sm">
              <Home className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-cinzel text-xs font-bold tracking-wider text-white">
                HOUSEHOLD & FAMILY LOGIN
              </h2>
              <span className="text-[10px] text-slate-400 font-mono">
                Tap your profile for instant Family & Camera controls
              </span>
            </div>
          </div>
          <button
            onClick={() => setAuthModalOpen(false)}
            className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Profile Cards */}
        <div className="flex-1 overflow-y-auto space-y-2.5 pr-0.5 no-scrollbar">
          {HOUSEHOLD_PROFILES.map((member) => {
            const isCurrent =
              activeId === member.id ||
              (member.id === 'lycan' && activeId === 'mom');
            const isBusy = loadingId === member.id;

            return (
              <button
                key={member.id}
                type="button"
                disabled={isBusy}
                onClick={() => handleSelectProfile(member)}
                className={`w-full p-3 rounded-2xl bg-gradient-to-r border text-left transition-all flex items-center justify-between gap-3 active:scale-[0.99] ${member.accentClass} ${
                  isCurrent ? 'ring-2 ring-white/30 shadow-lg' : 'opacity-90 hover:opacity-100'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-2xl bg-black/40 border border-white/15 flex items-center justify-center text-xl shrink-0">
                    {member.emoji}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-white truncate">
                        {member.displayName}
                      </span>
                      <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-black/40 border border-white/10 text-slate-200">
                        {member.badge}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-300/90 mt-0.5 leading-snug">
                      {isBusy ? 'Signing in & syncing cameras...' : member.subtitle}
                    </p>
                  </div>
                </div>

                <div className="shrink-0">
                  {isCurrent ? (
                    <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-[10px] font-bold">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Active
                    </span>
                  ) : (
                    <span className="px-2.5 py-1 rounded-xl bg-white/10 hover:bg-white/20 text-white text-[10px] font-bold">
                      Select
                    </span>
                  )}
                </div>
              </button>
            );
          })}

          {/* Family Priority Shortcuts */}
          <div className="p-3 rounded-2xl bg-white/5 border border-white/10 space-y-2 mt-1">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>Prioritized Family Controls</span>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <button
                type="button"
                onClick={() => {
                  setActiveLayer('family');
                  setActiveTab('cameras');
                  setAuthModalOpen(false);
                }}
                className="p-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 flex flex-col items-center gap-1 text-[10px] font-semibold"
              >
                <Video className="w-4 h-4" />
                <span>Cameras</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setAuthModalOpen(false);
                  setNotificationModalOpen(true);
                }}
                className="p-2 rounded-xl bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 text-sky-300 flex flex-col items-center gap-1 text-[10px] font-semibold"
              >
                <Bell className="w-4 h-4" />
                <span>Alert Prefs</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveLayer('family');
                  setActiveTab('devices');
                  setAuthModalOpen(false);
                }}
                className="p-2 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 flex flex-col items-center gap-1 text-[10px] font-semibold"
              >
                <Wrench className="w-4 h-4" />
                <span>Net & Fix</span>
              </button>
            </div>
          </div>

          {statusNote && (
            <div className="p-2 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[11px] text-center">
              {statusNote}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pt-2 border-t border-white/10 flex items-center justify-between text-xs">
          <button
            type="button"
            onClick={() => {
              setAuthModalOpen(false);
              setSettingsOpen(true);
            }}
            className="flex items-center gap-1.5 text-slate-400 hover:text-amber-300 transition-colors text-[11px]"
          >
            <Settings className="w-3.5 h-3.5 text-amber-400" />
            <span>Server & Network Settings</span>
          </button>
          <span className="text-[10px] text-slate-500 font-mono">v{CURRENT_APP_VERSION.version}</span>
        </div>
      </div>
    </div>
  );
};
